import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  Avatar,
  Switch,
  Divider,
  CircularProgress,
  Container,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  Grow,
  useTheme,
  useMediaQuery,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  Paper,
  Fade,
  Slide,
  Collapse,
  LinearProgress,
  FormControlLabel,
  Grid,
  IconButton,
} from '@mui/material';
import {
  Person as PersonIcon,
  Email as EmailIcon,
  CalendarToday as CalendarIcon,
  Schedule as ScheduleIcon,
  LocationOn as LocationOnIcon,
  Wc as GenderIcon,
  Notifications as NotificationsIcon,
  DarkMode as DarkModeIcon,
  Translate as TranslateIcon,
  History as HistoryIcon,
  Help as HelpIcon,
  Info as InfoIcon,
  Logout as LogoutIcon,
  ChevronRight as ChevronRightIcon,
  Chat as WeChatIcon,
  Launch as LaunchIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material';

import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useUser } from '../../context/UserContext';
import { BirthInfo } from '../../services/userApiService';
import { useTheme as useAppTheme } from '../../context/ThemeContext';
import { theme, responsiveSpacing, responsiveFontSizes } from '../../theme/theme';

const StyledCard = React.forwardRef<HTMLDivElement, { children: React.ReactNode }>(
  ({ children }, ref) => (
    <Card
      ref={ref}
      sx={{
        borderRadius: 0,
        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
        marginBottom: { xs: '32px', sm: '48px' },
      }}
    >
      {children}
    </Card>
  )
);

const GradientBox = React.forwardRef<HTMLDivElement, { children: React.ReactNode }>(
  ({ children }, ref) => (
    <Box
      ref={ref}
      sx={{
        background: (theme) => `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
        padding: { xs: responsiveSpacing.container, sm: responsiveSpacing.section },
        color: 'white',
        textAlign: 'center',
      }}
    >
      {children}
    </Box>
  )
);

const ProfileItem = React.forwardRef<HTMLDivElement, { children: React.ReactNode }>(
  ({ children }, ref) => (
    <Box
      ref={ref}
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: { xs: 'flex-start', sm: 'center' },
        padding: responsiveSpacing.xs,
        borderBottom: '1px solid',
        borderColor: 'divider',
        flexDirection: { xs: 'column', sm: 'row' },
        gap: { xs: responsiveSpacing.xs, sm: 0 },
      }}
    >
      {children}
    </Box>
  )
);

const ActionItem = React.forwardRef<HTMLDivElement, { children: React.ReactNode; isSignOut?: boolean; onClick?: () => void }>(
  ({ children, isSignOut, onClick }, ref) => (
    <Box
      ref={ref}
      onClick={onClick}
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: responsiveSpacing.xs,
        cursor: 'pointer',
        transition: 'all 0.3s ease',
        '&:hover': {
          backgroundColor: isSignOut ? 'rgba(244, 67, 54, 0.04)' : 'rgba(0, 0, 0, 0.04)',
        },
      }}
    >
      {children}
    </Box>
  )
);




const ProfileScreen = () => {
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const { userProfile, birthInfo, updateUserProfile, updateBirthInfo, generateAstrolabe, loading } = useUser();
  const { isDarkMode, toggleDarkMode } = useAppTheme();
  
  // Profile states
  const [displayName, setDisplayName] = useState(user?.displayName || '');
  const [email] = useState(user?.email || '');
  const [profileLoading, setProfileLoading] = useState(false);
  
  // Birth info states
  const [birthDate, setBirthDate] = useState<Date | null>(birthInfo?.birthDate ? new Date(birthInfo.birthDate) : new Date());
  const [birthTime, setBirthTime] = useState<Date | null>(birthInfo?.birthTime ? new Date(`2000-01-01 ${birthInfo.birthTime}`) : new Date());
  const [birthLocation, setBirthLocation] = useState(birthInfo?.birthLocation || '');
  const [gender, setGender] = useState(birthInfo?.gender || 'male');
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [showTimePicker, setShowTimePicker] = useState(false);
  const [birthInfoLoading, setBirthInfoLoading] = useState(false);
  
  // Settings states
  const [notifications, setNotifications] = useState(userProfile?.settings?.notifications ?? true);
  
  // Dialog states
  const [signOutDialogOpen, setSignOutDialogOpen] = useState(false);
  const [helpDialogOpen, setHelpDialogOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

  const handleUpdateProfile = async () => {
    if (!displayName.trim()) {
      setSnackbar({ open: true, message: '显示名称不能为空', severity: 'error' });
      return;
    }

    setProfileLoading(true);
    try {
      const updates = { displayName: displayName.trim() };
      
      console.log('Updating profile with:', updates);
      await updateUserProfile(updates);
      
      setSnackbar({ 
        open: true, 
        message: '个人资料更新成功', 
        severity: 'success' 
      });
      
    } catch (error: any) {
      console.error('Profile update error:', error);
      
      let errorMessage = '更新个人资料失败';
      
      if (error.message) {
        if (error.message.includes('network')) {
          errorMessage = '网络错误';
        } else if (error.message.includes('permission') || error.message.includes('unauthorized')) {
          errorMessage = '权限被拒绝';
        } else {
          errorMessage = error.message;
        }
      }
      
      setSnackbar({ open: true, message: errorMessage, severity: 'error' });
    } finally {
      setProfileLoading(false);
    }
  };



  const handleUpdateBirthInfo = async () => {
    if (!birthLocation.trim()) {
      setSnackbar({ open: true, message: '出生地点为必填项', severity: 'error' });
      return;
    }

    setBirthInfoLoading(true);
    try {
      const year = birthDate ? birthDate.getFullYear() : new Date().getFullYear();
      const month = birthDate ? birthDate.getMonth() + 1 : new Date().getMonth() + 1;
      const day = birthDate ? birthDate.getDate() : new Date().getDate();
      const hour = birthTime ? birthTime.getHours() : new Date().getHours();
      const minute = birthTime ? birthTime.getMinutes() : new Date().getMinutes();

      const birthInfoPayload: BirthInfo = {
        year,
        month,
        day,
        hour,
        minute,
        gender: (gender === 'male' || gender === 'female') ? gender : 'male',
        calendar: 'solar',
        location: birthLocation.trim(),
      };

      await updateBirthInfo(birthInfoPayload);
      setSnackbar({ open: true, message: '出生信息更新成功', severity: 'success' });
    } catch (error: any) {
      setSnackbar({ open: true, message: error.message, severity: 'error' });
    } finally {
      setBirthInfoLoading(false);
    }
  };

  const handleSignOut = async () => {
    try {
      await signOut();
    } catch (error) {
      setSnackbar({ open: true, message: '退出登录失败', severity: 'error' });
    }
    setSignOutDialogOpen(false);
  };

  const profileSections = [
    {
      title: '账户信息',
      items: [
        {
          icon: <PersonIcon />,
          label: '显示名称',
          value: displayName,
          editable: true,
          onChangeText: setDisplayName,
        },
        {
          icon: <EmailIcon />,
          label: '邮箱',
          value: email,
          editable: false,
        },
      ],
    },
    {
      title: '出生信息',
      items: [
        {
          icon: <CalendarIcon />,
          label: '出生日期',
          value: birthDate?.toLocaleDateString() || '',
          onPress: () => setShowDatePicker(true),
        },
        {
          icon: <ScheduleIcon />,
          label: '出生时间',
          value: birthTime?.toTimeString().split(' ')[0].substring(0, 5) || '',
          onPress: () => setShowTimePicker(true),
        },
        {
          icon: <LocationOnIcon />,
          label: '出生地点',
          value: birthLocation,
          editable: true,
          onChangeText: setBirthLocation,
          placeholder: '请输入出生城市',
        },
        {
          icon: <GenderIcon />,
          label: '性别',
          value: gender === 'male' ? '男' : '女',
          onPress: () => setGender(gender === 'male' ? 'female' : 'male'),
        },
      ],
    },
    {
      title: '设置',
      items: [
        {
          icon: <NotificationsIcon />,
          label: '通知',
          value: notifications,
          type: 'switch',
          onValueChange: setNotifications,
        },
        {
            icon: <DarkModeIcon />,
            label: '深色模式',
            value: isDarkMode,
            type: 'switch',
            onValueChange: toggleDarkMode,
          },
      ],
    },
  ];

  const renderProfileItem = (item: any, index: number) => {
    return (
      <ProfileItem key={index}>
        <Box sx={{ display: 'flex', alignItems: 'center', flex: 1, minWidth: 0 }}>
          {React.cloneElement(item.icon, { 
            sx: { 
              color: 'primary.main', 
              mr: responsiveSpacing.xs,
              fontSize: { xs: '1.2rem', sm: '1.5rem' }
            } 
          })}
          <Typography 
            variant="body1" 
            sx={{ 
              color: 'text.primary',
              fontSize: responsiveFontSizes.body1,
              fontWeight: 500
            }}
          >
            {item.label}
          </Typography>
        </Box>
        
        <Box sx={{ 
          flex: { xs: 'none', sm: 1 }, 
          display: 'flex', 
          justifyContent: { xs: 'flex-start', sm: 'flex-end' },
          width: { xs: '100%', sm: 'auto' },
          mt: { xs: 1, sm: 0 }
        }}>
          {item.editable ? (
            <TextField
              value={item.value}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => item.onChangeText(e.target.value)}
              placeholder={item.placeholder}
              variant="outlined"
              size="small"
              sx={{ 
                width: { xs: '100%', sm: 'auto' },
                maxWidth: { sm: 200 },
                '& .MuiInputBase-input': {
                  fontSize: responsiveFontSizes.body2
                }
              }}
            />
          ) : item.type === 'switch' ? (
            <Switch
              checked={item.value}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => item.onValueChange(e.target.checked)}
              color="primary"
            />
          ) : (
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              cursor: item.onPress ? 'pointer' : 'default' 
            }} onClick={item.onPress}>
              <Typography 
                variant="body2" 
                sx={{ 
                  color: 'text.secondary', 
                  mr: responsiveSpacing.xs,
                  fontSize: responsiveFontSizes.body2
                }}
              >
                {item.value}
              </Typography>
              {item.onPress && (
                <ChevronRightIcon sx={{ color: theme.colors.placeholder }} />
              )}
            </Box>
          )}
        </Box>
      </ProfileItem>
    );
  };

  return (
      <Box sx={{ minHeight: '100vh', backgroundColor: theme.colors.background }}>
        {/* Header */}
        <GradientBox>
          <Avatar
            src={user?.photoURL || undefined}
            sx={{
              width: { xs: 60, sm: 80, md: 100 },
              height: { xs: 60, sm: 80, md: 100 },
              backgroundColor: theme.colors.accent,
              margin: '0 auto',
              mb: responsiveSpacing.xs,
              fontSize: { xs: '1.5rem', sm: '2rem', md: '2.5rem' },
              border: '3px solid white',
            }}
          >
            {!user?.photoURL && (user?.displayName?.charAt(0) || 'U')}
          </Avatar>
          
          <Typography 
            variant="h4" 
            sx={{ 
              fontWeight: 'bold', 
              color: '#FFFFFF', 
              mb: responsiveSpacing.xs,
              fontSize: responsiveFontSizes.h4
            }}
          >
            {user?.displayName || 'User'}
          </Typography>
          <Typography 
            variant="h6" 
            sx={{ 
              color: '#F5F5DC', 
              opacity: 0.9,
              fontSize: responsiveFontSizes.h6,
              wordBreak: 'break-word'
            }}
          >
            {user?.email}
          </Typography>
        </GradientBox>

        <Container 
          maxWidth="md" 
          sx={{ 
            py: responsiveSpacing.section, 
            mt: `-${responsiveSpacing.xs}`,
            px: { xs: responsiveSpacing.xs, sm: responsiveSpacing.component }
          }}
        >
          {/* Profile Sections */}
          {profileSections.map((section, sectionIndex) => (
            <Grow in timeout={800} style={{ transitionDelay: `${sectionIndex * 200}ms` }} key={sectionIndex}>
              <StyledCard>
                <CardContent sx={{ p: responsiveSpacing.component }}>
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      mb: responsiveSpacing.xs, 
                      fontWeight: 'bold', 
                      color: 'text.primary',
                      fontSize: responsiveFontSizes.h6
                    }}
                  >
                    {section.title}
                  </Typography>
                  {section.items.map(renderProfileItem)}
                  
                  {section.title === '账户信息' && (
                    <Button
                      variant="contained"
                      onClick={handleUpdateProfile}
                      disabled={profileLoading}
                      sx={{
                        mt: responsiveSpacing.component,
                        borderRadius: 20,
                        backgroundColor: theme.colors.primary,
                        '&:hover': { backgroundColor: theme.colors.primary + 'dd' },
                      }}
                    >
                      {profileLoading ? <CircularProgress size={20} color="inherit" /> : '更新个人资料'}
                    </Button>
                  )}
                  
                  {section.title === '出生信息' && (
                    <Button
                      variant="contained"
                      onClick={handleUpdateBirthInfo}
                      disabled={birthInfoLoading || loading}
                      sx={{
                        mt: responsiveSpacing.component,
                        borderRadius: 20,
                        backgroundColor: theme.colors.primary,
                        '&:hover': { backgroundColor: theme.colors.primary + 'dd' },
                      }}
                    >
                      {birthInfoLoading || loading ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <CircularProgress size={20} color="inherit" />
                          {loading ? '生成星盘中' : '更新中'}
                        </Box>
                      ) : (
                        '更新出生信息'
                      )}
                    </Button>
                  )}
                </CardContent>
              </StyledCard>
            </Grow>
          ))}

          {/* Action Buttons */}
          <Grow in timeout={800} style={{ transitionDelay: '600ms' }}>
            <StyledCard>
              <CardContent sx={{ p: responsiveSpacing.component }}>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    fontWeight: 'bold', 
                    color: 'primary.main', 
                    mb: responsiveSpacing.xs,
                    fontSize: responsiveFontSizes.h6
                  }}
                >
                  操作
                </Typography>
                
                <ActionItem onClick={() => navigate('/consultation/history')}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <HistoryIcon sx={{ 
                      color: 'primary.main', 
                      mr: responsiveSpacing.xs,
                      fontSize: { xs: '1.2rem', sm: '1.5rem' }
                    }} />
                    <Typography 
                      variant="body1" 
                      sx={{ 
                        color: 'text.primary',
                        fontSize: responsiveFontSizes.body1,
                        fontWeight: 500
                      }}
                    >
                      咨询历史
                    </Typography>
                  </Box>
                  <ChevronRightIcon sx={{ color: 'text.secondary' }} />
                </ActionItem>
                
                <Divider sx={{ backgroundColor: 'divider' }} />
                
                <ActionItem onClick={() => navigate('/subscription-management')}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <SettingsIcon sx={{ 
                      color: 'primary.main', 
                      mr: responsiveSpacing.xs,
                      fontSize: { xs: '1.2rem', sm: '1.5rem' }
                    }} />
                    <Typography 
                      variant="body1" 
                      sx={{ 
                        color: 'text.primary',
                        fontSize: responsiveFontSizes.body1,
                        fontWeight: 500
                      }}
                    >
                      订阅管理
                    </Typography>
                  </Box>
                  <ChevronRightIcon sx={{ color: 'text.secondary' }} />
                </ActionItem>
                
                <Divider sx={{ backgroundColor: 'divider' }} />
                
                <ActionItem onClick={() => setHelpDialogOpen(true)}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <HelpIcon sx={{ 
                      color: 'primary.main', 
                      mr: responsiveSpacing.xs,
                      fontSize: { xs: '1.2rem', sm: '1.5rem' }
                    }} />
                    <Typography 
                      variant="body1" 
                      sx={{ 
                        color: 'text.primary',
                        fontSize: responsiveFontSizes.body1,
                        fontWeight: 500
                      }}
                    >
                      帮助
                    </Typography>
                  </Box>
                  <ChevronRightIcon sx={{ color: 'text.secondary' }} />
                </ActionItem>
                
                <Divider sx={{ backgroundColor: 'divider' }} />
                
                <ActionItem onClick={() => setSnackbar({ open: true, message: '关于应用', severity: 'success' })}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <InfoIcon sx={{ 
                      color: 'primary.main', 
                      mr: responsiveSpacing.xs,
                      fontSize: { xs: '1.2rem', sm: '1.5rem' }
                    }} />
                    <Typography 
                      variant="body1" 
                      sx={{ 
                        color: 'text.primary',
                        fontSize: responsiveFontSizes.body1,
                        fontWeight: 500
                      }}
                    >
                      关于
                    </Typography>
                  </Box>
                  <ChevronRightIcon sx={{ color: 'text.secondary' }} />
                </ActionItem>
                
                <Divider sx={{ backgroundColor: 'divider' }} />
                
                <ActionItem isSignOut onClick={() => setSignOutDialogOpen(true)}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <LogoutIcon sx={{ 
                      color: 'error.main', 
                      mr: responsiveSpacing.xs,
                      fontSize: { xs: '1.2rem', sm: '1.5rem' }
                    }} />
                    <Typography 
                      variant="body1" 
                      sx={{ 
                        color: 'error.main',
                        fontSize: responsiveFontSizes.body1,
                        fontWeight: 500
                      }}
                    >
                      退出登录
                    </Typography>
                  </Box>
                  <ChevronRightIcon sx={{ color: 'error.main' }} />
                </ActionItem>
              </CardContent>
            </StyledCard>
          </Grow>
        </Container>

        {/* Date Picker Dialog */}
        <Dialog 
          open={showDatePicker} 
          onClose={() => setShowDatePicker(false)}
          fullWidth
          maxWidth="sm"
        >
          <DialogTitle sx={{ fontSize: responsiveFontSizes.h6 }}>
            选择出生日期
          </DialogTitle>
          <DialogContent sx={{ p: responsiveSpacing.component }}>
            <TextField
              type="date"
              fullWidth
              margin="normal"
              value={birthDate ? birthDate.toISOString().split('T')[0] : ''}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setBirthDate(e.target.value ? new Date(e.target.value) : null)}
              inputProps={{ max: new Date().toISOString().split('T')[0] }}
              sx={{
                '& .MuiInputBase-input': {
                  fontSize: responsiveFontSizes.body1
                }
              }}
            />
          </DialogContent>
          <DialogActions sx={{ p: responsiveSpacing.component }}>
            <Button 
              onClick={() => setShowDatePicker(false)}
              sx={{ fontSize: responsiveFontSizes.body2 }}
            >
              取消
            </Button>
            <Button 
              onClick={() => setShowDatePicker(false)} 
              variant="contained"
              sx={{ fontSize: responsiveFontSizes.body2 }}
            >
              确定
            </Button>
          </DialogActions>
        </Dialog>

        {/* Time Picker Dialog */}
        <Dialog 
          open={showTimePicker} 
          onClose={() => setShowTimePicker(false)}
          fullWidth
          maxWidth="sm"
        >
          <DialogTitle sx={{ fontSize: responsiveFontSizes.h6 }}>
            选择出生时间
          </DialogTitle>
          <DialogContent sx={{ p: responsiveSpacing.component }}>
            <TextField
              type="time"
              fullWidth
              margin="normal"
              value={birthTime ? birthTime.toTimeString().split(' ')[0].substring(0, 5) : ''}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                if (e.target.value) {
                  const [hours, minutes] = e.target.value.split(':');
                  const date = new Date();
                  date.setHours(parseInt(hours), parseInt(minutes), 0, 0);
                  setBirthTime(date);
                } else {
                  setBirthTime(null);
                }
              }}
              sx={{
                '& .MuiInputBase-input': {
                  fontSize: responsiveFontSizes.body1
                }
              }}
            />
          </DialogContent>
          <DialogActions sx={{ p: responsiveSpacing.component }}>
            <Button 
              onClick={() => setShowTimePicker(false)}
              sx={{ fontSize: responsiveFontSizes.body2 }}
            >
              取消
            </Button>
            <Button 
              onClick={() => setShowTimePicker(false)} 
              variant="contained"
              sx={{ fontSize: responsiveFontSizes.body2 }}
            >
              确定
            </Button>
          </DialogActions>
        </Dialog>

        {/* Sign Out Confirmation Dialog */}
        <Dialog 
          open={signOutDialogOpen} 
          onClose={() => setSignOutDialogOpen(false)}
          fullWidth
          maxWidth="sm"
        >
          <DialogTitle sx={{ fontSize: responsiveFontSizes.h6 }}>
            退出登录
          </DialogTitle>
          <DialogContent sx={{ p: responsiveSpacing.component }}>
            <Typography sx={{ fontSize: responsiveFontSizes.body1 }}>
              确定要退出登录吗？
            </Typography>
          </DialogContent>
          <DialogActions sx={{ p: responsiveSpacing.component }}>
            <Button 
              onClick={() => setSignOutDialogOpen(false)}
              sx={{ fontSize: responsiveFontSizes.body2 }}
            >
              取消
            </Button>
            <Button 
              onClick={handleSignOut} 
              color="error" 
              variant="contained"
              sx={{ fontSize: responsiveFontSizes.body2 }}
            >
              退出登录
            </Button>
          </DialogActions>
        </Dialog>

        {/* Help Dialog */}
        <Dialog 
          open={helpDialogOpen} 
          onClose={() => setHelpDialogOpen(false)}
          fullWidth
          maxWidth="sm"
        >
          <DialogTitle sx={{ 
            fontSize: responsiveFontSizes.h6,
            textAlign: 'center',
            pb: responsiveSpacing.xs
          }}>
            帮助
          </DialogTitle>
          <DialogContent sx={{ p: responsiveSpacing.component }}>
            <Typography 
              variant="body1" 
              sx={{ 
                fontSize: responsiveFontSizes.body1,
                mb: responsiveSpacing.component,
                textAlign: 'center',
                color: 'text.secondary'
              }}
            >
              联系方式
            </Typography>
            
            {/* Contact Information */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: responsiveSpacing.component }}>
              {/* WeChat */}
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                p: responsiveSpacing.xs,
                borderRadius: 2,
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
                transition: 'all 0.3s ease',
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.08)',
                }
              }}>
                <WeChatIcon sx={{ 
                  color: '#07C160', 
                  mr: responsiveSpacing.xs,
                  fontSize: { xs: '1.5rem', sm: '1.8rem' }
                }} />
                <Box>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      fontSize: responsiveFontSizes.body2,
                      fontWeight: 600,
                      color: 'text.primary'
                    }}
                  >
                    微信
                  </Typography>
                  <Typography 
                    variant="body1" 
                    sx={{ 
                      fontSize: responsiveFontSizes.body1,
                      color: 'text.secondary',
                      fontFamily: 'monospace'
                    }}
                  >
                    ljndy01
                  </Typography>
                </Box>
              </Box>

              {/* Email */}
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                p: responsiveSpacing.xs,
                borderRadius: 2,
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
                transition: 'all 0.3s ease',
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.08)',
                }
              }}>
                <EmailIcon sx={{ 
                  color: '#EA4335', 
                  mr: responsiveSpacing.xs,
                  fontSize: { xs: '1.5rem', sm: '1.8rem' }
                }} />
                <Box>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      fontSize: responsiveFontSizes.body2,
                      fontWeight: 600,
                      color: 'text.primary'
                    }}
                  >
                    邮箱
                  </Typography>
                  <Typography 
                    variant="body1" 
                    sx={{ 
                      fontSize: responsiveFontSizes.body1,
                      color: 'text.secondary',
                      fontFamily: 'monospace'
                    }}
                  >
        https://ziwei.jieqirili.com
                  </Typography>
                </Box>
              </Box>

              {/* X (Twitter) */}
              <Box 
                onClick={() => window.open('https://x.com/fan_ze_jia?t=KUJl7wQNGZPajjp9hsBZrQ&s=09', '_blank')}
                sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  p: responsiveSpacing.xs,
                  borderRadius: 2,
                  backgroundColor: 'rgba(0, 0, 0, 0.04)',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.08)',
                    transform: 'translateY(-1px)'
                  }
                }}
              >
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  width: { xs: '1.5rem', sm: '1.8rem' },
                  height: { xs: '1.5rem', sm: '1.8rem' },
                  mr: responsiveSpacing.xs,
                  backgroundColor: '#000',
                  borderRadius: '50%'
                }}>
                  <Typography sx={{ 
                    color: 'white', 
                    fontSize: { xs: '0.8rem', sm: '1rem' },
                    fontWeight: 'bold'
                  }}>
                    X
                  </Typography>
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      fontSize: responsiveFontSizes.body2,
                      fontWeight: 600,
                      color: 'text.primary'
                    }}
                  >
                    X
                  </Typography>
                  <Typography 
                    variant="body1" 
                    sx={{ 
                      fontSize: responsiveFontSizes.body1,
                      color: 'text.secondary'
                    }}
                  >
                    @fan_ze_jia
                  </Typography>
                </Box>
                <LaunchIcon sx={{ 
                  color: 'text.secondary',
                  fontSize: { xs: '1rem', sm: '1.2rem' }
                }} />
              </Box>
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: responsiveSpacing.component, justifyContent: 'center' }}>
            <Button 
              onClick={() => setHelpDialogOpen(false)}
              variant="contained"
              sx={{ 
                fontSize: responsiveFontSizes.body2,
                borderRadius: 20,
                px: responsiveSpacing.component
              }}
            >
              关闭
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar for notifications */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          <Alert
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            severity={snackbar.severity}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
  );
};

export default ProfileScreen;